import { Link } from 'react-router-dom';
import {
  FileText, Receipt, Building2, ShieldCheck, Users, TrendingUp,
  ArrowRight, CheckCircle2, Phone, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';

const services = [
  { icon: FileText, title: 'Income Tax Filing', desc: 'Expert ITR filing for individuals, HUFs, and businesses. Maximize deductions and ensure full compliance.' },
  { icon: Receipt, title: 'GST Services', desc: 'Complete GST registration, monthly returns (GSTR-1, GSTR-3B), amendments, and compliance management.' },
  { icon: Building2, title: 'Company Registration', desc: 'Pvt Ltd, LLP, Partnership, and Sole Proprietorship registration with end-to-end documentation.' },
  { icon: ShieldCheck, title: 'Professional Tax', desc: 'PT registration, annual filing, and compliance for employers and professionals in Karnataka.' },
  { icon: Users, title: 'Audit & Assurance', desc: 'Statutory audits, tax audits, internal audits, and compliance reviews for businesses of all sizes.' },
  { icon: TrendingUp, title: 'Business Advisory', desc: 'Strategic financial planning, business structuring, and growth advisory for startups and SMEs.' },
];

const stats = [
  { value: '200+', label: 'Clients Served' },
  { value: '20+', label: 'Years Experience' },
  { value: '5000+', label: 'Returns Filed' },
  { value: '100%', label: 'Compliance Rate' },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#1B2A1B] min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwY29ycG9yYXRlJTIwbW9kZXJufGVufDB8fHx8MTc3NjMyODUxNXww&ixlib=rb-4.1.0&q=85"
            alt="Professional team"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B2A1B] via-[#1B2A1B]/90 to-[#1B2A1B]/60" />
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full mb-6">
              <Star size={12} className="text-[#D4AF37]" />
              <span className="text-xs text-[#D4AF37] font-medium tracking-wider uppercase">Business Strategy, Consulting &amp; Development Consultants Since 2004</span>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold text-white leading-[1.1] tracking-tight mb-6">
              Your Business<br />
              <span className="text-[#D4AF37]">Solutions</span><br />
              Partner
            </h1>
            <p className="text-lg text-olive-300 leading-relaxed mb-8 max-w-lg">
              SVT & Co provides comprehensive tax consulting, compliance, and business advisory services
              to over 200 clients across Bengaluru and beyond.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/svt-co/contact">
                <Button className="h-12 px-8 bg-[#D4AF37] text-[#1B2A1B] hover:bg-[#C5A028] font-semibold text-sm rounded-sm gap-2" data-testid="hero-cta-btn">
                  Book Consultation <ArrowRight size={16} />
                </Button>
              </Link>
              <Link to="/svt-co/about">
                <Button variant="outline" className="h-12 px-8 border-olive-500 text-white hover:bg-white/10 font-medium text-sm rounded-sm">
                  Learn About Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#F4F7F4] border-y border-[#E6EDE6]">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map(s => (
              <div key={s.label} className="text-center">
                <p className="font-heading text-4xl font-semibold text-[#1B2A1B]">{s.value}</p>
                <p className="text-sm text-olive-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-6 py-20" id="services">
        <div className="text-center mb-14">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#D4AF37] mb-3">What We Do</p>
          <h2 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Our Services</h2>
          <p className="text-sm text-olive-500 mt-3 max-w-lg mx-auto">
            Comprehensive tax and business solutions tailored for individuals, SMEs, and enterprises.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map(service => (
            <div
              key={service.title}
              className="group bg-white border border-[#E6EDE6] rounded-sm p-7 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              data-testid={`service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-11 h-11 rounded-sm bg-[#1B2A1B] flex items-center justify-center mb-5 group-hover:bg-[#D4AF37] transition-colors duration-300">
                <service.icon size={20} className="text-white" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg font-medium text-[#111B11] mb-2">{service.title}</h3>
              <p className="text-sm text-olive-500 leading-relaxed">{service.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-[#1B2A1B] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#D4AF37] mb-3">Why SVT & Co</p>
              <h2 className="font-heading text-4xl font-semibold text-white tracking-tight mb-8">
                Trusted by 200+ Clients Across Bengaluru
              </h2>
              <div className="space-y-5">
                {[
                  'Personalized service — every client gets a dedicated consultant',
                  'On-time filing — zero penalties across 5000+ returns',
                  'Complete transparency — no hidden fees, clear pricing',
                  'Technology-driven — modern portal for real-time filing status',
                  'Located in Whitefield — serving Bengaluru\'s IT corridor',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-olive-200 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758518727707-b023e285b709?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwY29ycG9yYXRlJTIwbW9kZXJufGVufDB8fHx8MTc3NjMyODUxNXww&ixlib=rb-4.1.0&q=85"
                alt="SVT & Co team"
                className="rounded-sm shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] text-[#1B2A1B] p-5 rounded-sm shadow-lg">
                <p className="font-heading text-3xl font-semibold">20+</p>
                <p className="text-sm font-medium">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="bg-[#F4F7F4] border border-[#E6EDE6] rounded-sm p-10 md:p-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#111B11] tracking-tight mb-4">
            Ready to Simplify Your Taxes?
          </h2>
          <p className="text-sm text-olive-500 mb-8 max-w-md mx-auto">
            Book a free consultation with our experts. We'll review your tax situation and recommend the best approach.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/svt-co/contact">
              <Button className="h-12 px-8 bg-[#D4AF37] text-[#1B2A1B] hover:bg-[#C5A028] font-semibold rounded-sm gap-2" data-testid="cta-book-btn">
                Book Free Consultation <ArrowRight size={16} />
              </Button>
            </Link>
            <a href="tel:+919876543210">
              <Button variant="outline" className="h-12 px-8 border-olive-300 text-[#1B2A1B] hover:bg-olive-50 font-medium rounded-sm gap-2">
                <Phone size={16} /> Call Us Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
